import { routes } from '@/config/routes';
import type { CatalogProduct, CategoryConfig } from '@/features/catalog/types/catalog';

const woodImage = '/assets/catalog/wood-keepsake.svg';
const ornamentImage = '/assets/catalog/ornament-keepsake.svg';
const shirtImage = '/assets/catalog/shirt-keepsake.svg';

export const categoryConfigs: CategoryConfig[] = [
  {
    slug: 'wood-engraving',
    title: 'Custom Wood Engraving',
    shortTitle: 'Wood Engraving',
    eyebrow: 'Naturally meaningful',
    description: 'Warm wood keepsakes engraved with the names, dates, and artwork you treasure.',
    heroMessage: 'A lasting story, thoughtfully etched in wood.',
    customizationNotes: [
      'Add names, dates, messages, or artwork.',
      'Preview guidance will arrive with the M4 customizer.',
      'Natural grain makes every finished piece subtly unique.',
    ],
    icon: 'pi pi-box',
    route: routes.woodEngraving,
  },
  {
    slug: 'ornaments',
    title: 'Personalized Christmas Ornaments',
    shortTitle: 'Ornaments',
    eyebrow: 'Traditions made personal',
    description:
      'Create a small, heartfelt marker for the people and moments that shaped your year.',
    heroMessage: 'Give every holiday memory a place on the tree.',
    customizationNotes: [
      'Personalize names, milestones, and short messages.',
      'Seasonal availability will be confirmed before ordering.',
      'The guided ornament customizer is planned for M5.',
    ],
    icon: 'pi pi-star',
    route: routes.ornaments,
  },
  {
    slug: 'tshirts',
    title: 'Custom Printed T-Shirts',
    shortTitle: 'Custom T-Shirts',
    eyebrow: 'Wear the moment',
    description: 'Personalized apparel for reunions, celebrations, teams, and everyday expression.',
    heroMessage: 'Turn your idea into something everyone can wear.',
    customizationNotes: [
      'Add photos, artwork, event details, or team names.',
      'Final garment colors and size guides will be product-specific.',
      'The apparel design tool is planned for M6.',
    ],
    icon: 'pi pi-palette',
    route: routes.tshirts,
  },
];

export const mockCatalog: CatalogProduct[] = [
  {
    id: 'wood-001',
    slug: 'family-portrait-plaque',
    category: 'wood-engraving',
    name: 'Personalized Family Portrait Plaque',
    shortDescription: 'A warm illustrated family keepsake engraved on natural wood.',
    description:
      'Celebrate the people who make home feel like home with a personalized portrait-style plaque, prepared for engraving on natural wood.',
    startingPrice: 48,
    currency: 'USD',
    imageUrl: woodImage,
    imageAlt: 'Illustrated wooden plaque with engraved family silhouettes',
    badges: ['bestseller'],
    featured: true,
    customizable: true,
    active: true,
  },
  {
    id: 'wood-002',
    slug: 'wedding-keepsake',
    category: 'wood-engraving',
    name: 'Engraved Wedding Keepsake',
    shortDescription: 'Names, a date, and a quiet reminder of a day worth keeping.',
    description:
      'A refined engraved keepsake designed to hold a couple’s names, wedding date, and a short personal message.',
    startingPrice: 56,
    currency: 'USD',
    imageUrl: woodImage,
    imageAlt: 'Wood wedding keepsake engraved with botanical details',
    badges: ['popular'],
    featured: true,
    customizable: true,
    active: true,
  },
  {
    id: 'wood-003',
    slug: 'recipe-board',
    category: 'wood-engraving',
    name: 'Handwritten Recipe Board',
    shortDescription: 'Preserve a favorite family recipe in the handwriting you remember.',
    description:
      'Transform a treasured handwritten recipe into an engraved display board made for kitchens and family gatherings.',
    startingPrice: 64,
    currency: 'USD',
    imageUrl: woodImage,
    imageAlt: 'Wooden display board engraved with a handwritten recipe',
    badges: ['new'],
    featured: false,
    customizable: true,
    active: true,
  },
  {
    id: 'wood-004',
    slug: 'pet-memory-frame',
    category: 'wood-engraving',
    name: 'Pet Memory Frame',
    shortDescription: 'A gentle tribute with a name, dates, and a favorite silhouette.',
    description:
      'Honor a beloved companion with a personalized wood frame designed around a simple portrait and meaningful details.',
    startingPrice: 42,
    currency: 'USD',
    imageUrl: woodImage,
    imageAlt: 'Engraved wood pet memorial frame with paw detail',
    badges: [],
    featured: false,
    customizable: true,
    active: true,
  },
  {
    id: 'orn-001',
    slug: 'babys-first-christmas',
    category: 'ornaments',
    name: "Baby's First Christmas Ornament",
    shortDescription: 'A sweet first-holiday marker personalized for your newest arrival.',
    description:
      'Commemorate a first Christmas with a name, year, and delicate seasonal design made to return to the tree each year.',
    startingPrice: 22,
    currency: 'USD',
    imageUrl: ornamentImage,
    imageAlt: 'Round personalized first Christmas ornament with stars',
    badges: ['bestseller', 'seasonal'],
    featured: true,
    customizable: true,
    active: true,
  },
  {
    id: 'orn-002',
    slug: 'family-name-ornament',
    category: 'ornaments',
    name: 'Custom Family Name Ornament',
    shortDescription: 'A classic family-name design for shared holiday traditions.',
    description:
      'Gather the household into one personalized ornament with a family name, year, and understated festive details.',
    startingPrice: 20,
    currency: 'USD',
    imageUrl: ornamentImage,
    imageAlt: 'Personalized family name ornament hanging from a ribbon',
    badges: ['popular', 'seasonal'],
    featured: true,
    customizable: true,
    active: true,
  },
  {
    id: 'orn-003',
    slug: 'new-home-ornament',
    category: 'ornaments',
    name: 'Our New Home Ornament',
    shortDescription: 'Remember the year a new address became home.',
    description:
      'Mark a move, first home, or fresh beginning with an address-inspired ornament personalized with names and year.',
    startingPrice: 24,
    currency: 'USD',
    imageUrl: ornamentImage,
    imageAlt: 'House-shaped new home ornament with personalized year',
    badges: ['new', 'seasonal'],
    featured: false,
    customizable: true,
    active: true,
  },
  {
    id: 'orn-004',
    slug: 'pet-holiday-ornament',
    category: 'ornaments',
    name: 'Pet Holiday Ornament',
    shortDescription: 'Give a favorite four-legged family member a place on the tree.',
    description:
      'Create a cheerful holiday ornament with your pet’s name, a simple silhouette, and an optional year.',
    startingPrice: 21,
    currency: 'USD',
    imageUrl: ornamentImage,
    imageAlt: 'Personalized holiday pet ornament with paw print',
    badges: ['seasonal'],
    featured: false,
    customizable: true,
    active: true,
  },
  {
    id: 'shirt-001',
    slug: 'personalized-photo-tshirt',
    category: 'tshirts',
    name: 'Personalized Photo T-Shirt',
    shortDescription: 'Put a favorite face or memory at the center of a wearable design.',
    description:
      'Build a custom shirt around a meaningful photo, supported by simple text and layout options in the future apparel editor.',
    startingPrice: 29,
    currency: 'USD',
    imageUrl: shirtImage,
    imageAlt: 'Cream T-shirt mockup with a framed custom photo area',
    badges: ['bestseller'],
    featured: true,
    customizable: true,
    active: true,
  },
  {
    id: 'shirt-002',
    slug: 'custom-event-shirt',
    category: 'tshirts',
    name: 'Custom Event Shirt',
    shortDescription: 'Bring reunions, trips, and celebrations together with one design.',
    description:
      'Create a coordinated event shirt with names, dates, and artwork prepared for groups of every size.',
    startingPrice: 26,
    currency: 'USD',
    imageUrl: shirtImage,
    imageAlt: 'Terracotta event T-shirt mockup with customizable lettering',
    badges: ['popular'],
    featured: true,
    customizable: true,
    active: true,
  },
  {
    id: 'shirt-003',
    slug: 'small-business-logo-shirt',
    category: 'tshirts',
    name: 'Small Business Logo Shirt',
    shortDescription: 'A clean branded shirt foundation for teams and pop-up events.',
    description:
      'Turn approved brand artwork into a practical team shirt with placement and garment choices planned for the M6 editor.',
    startingPrice: 32,
    currency: 'USD',
    imageUrl: shirtImage,
    imageAlt: 'Dark T-shirt mockup with a small business logo placement',
    badges: ['new'],
    featured: false,
    customizable: true,
    active: true,
  },
  {
    id: 'shirt-004',
    slug: 'family-reunion-shirt',
    category: 'tshirts',
    name: 'Family Reunion T-Shirt',
    shortDescription: 'Celebrate the names, places, and stories that bring everyone back.',
    description:
      'A flexible reunion-shirt concept for family names, gathering locations, years, and group-friendly artwork.',
    startingPrice: 27,
    currency: 'USD',
    imageUrl: shirtImage,
    imageAlt: 'Green family reunion T-shirt mockup with tree artwork',
    badges: [],
    featured: false,
    customizable: true,
    active: true,
  },
];

export function getCategoryConfig(slug: string | undefined) {
  return categoryConfigs.find((category) => category.slug === slug);
}

export function getProduct(categorySlug: string | undefined, productSlug: string | undefined) {
  return mockCatalog.find(
    (product) =>
      product.category === categorySlug && product.slug === productSlug && product.active,
  );
}
